'use client'

import type { ReactNode } from 'react'

import { ImageWrapper } from '@/components/image'
import type { BrandLabProduct } from '@/lib/supabase/brand-modules/types'

import { LabFitToggle } from './lab-fit-toggle'
import { LabScoreBar } from './lab-score-bar'
import { useLabFitScores } from './use-lab-fit-scores'

export interface LabScoreCardLabels {
  categories: Record<string, string>
  initialFit: string
  tunedFit: string
  categoryHeadingInitial: string
  categoryHeadingTuned: string
  soundScore: string
  fullReport: string
}

interface LabScoreCardProps {
  product: BrandLabProduct
  labels: LabScoreCardLabels
  reportHref: string | null
  /** Server-rendered HearAdvisor logo lockup shown above the product name
   *  (the article blok passes it; the brand hub band attributes in its own
   *  header instead). */
  attribution?: ReactNode
}

export const LabScoreCard = ({ product, labels, reportHref, attribution }: LabScoreCardProps) => {
  const { scores, activeFit, setSelectedFit } = useLabFitScores(product)
  const categoryHeading = activeFit === 'tuned' ? labels.categoryHeadingTuned : labels.categoryHeadingInitial

  return (
    <div className="h-fit rounded-2xl border border-white/12 bg-white/6 p-5">
      {/* Product photos are shot on white — give them a white chip, same as
          the fact card's logo treatment. */}
      {product.image && (
        <div className="mb-4 flex h-32 items-center justify-center rounded-xl bg-white px-6 py-4">
          <ImageWrapper
            image={product.image}
            origin="brand-lab-product"
            loadTime="lazy"
            imgProps={{ alt: product.label, className: 'h-full w-auto max-w-full object-contain' }}
            sizingProps={{ fixed: false, minWidth: 200, maxWidth: 480, sizes: '320px' }}
          />
        </div>
      )}

      {/* flex, not block: the wrapper would otherwise inherit the article's
          prose-xl line-height and open a tall line box under the lockup. */}
      {attribution && <div className="mb-1.5 flex">{attribution}</div>}
      <p className="text-md font-semibold text-white">
        {product.label} <span className="font-normal">— {categoryHeading}</span>
      </p>

      <div className="mt-4 flex flex-col gap-3">
        {scores[activeFit].map((category) => (
          <LabScoreBar key={category.id} label={labels.categories[category.id] ?? category.id} score={category.score} />
        ))}
      </div>

      <LabFitToggle
        className="mt-5"
        activeFit={activeFit}
        availability={{ initial: scores.initial.length > 0, tuned: scores.tuned.length > 0 }}
        labels={{ initial: labels.initialFit, tuned: labels.tunedFit }}
        ariaLabel={categoryHeading}
        onSelect={setSelectedFit}
      />

      <p className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-white/12 pt-4 text-sm text-white/60">
        {product.soundScore != null && (
          <>
            <span className="bg-sky-80/20 text-sky-80 inline-flex h-7 min-w-7 items-center justify-center rounded-lg px-1.5 text-sm font-bold">
              {product.soundScore.toFixed(1)}
            </span>
            <span>{labels.soundScore}</span>
          </>
        )}
        {reportHref && (
          <a href={reportHref} className="text-sky-80 font-semibold hover:underline" data-tracking="ignore">
            {labels.fullReport} →
          </a>
        )}
      </p>
    </div>
  )
}
